/**
 * VoiceIncidentModal — caregiver dictates an incident, AI classifies + extracts
 * the appropriate report (fall / injury / behaviour / medication / hospital
 * transfer / body check). User reviews the proposed report and submits, which
 * POSTs to the matching backend endpoint.
 */
import { useState } from "react";
import { createPortal } from "react-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import { Mic, Square, Loader2, Sparkles, X, AlertOctagon, AlertTriangle, Activity, Pill, Ambulance, ListChecks, Smile } from "lucide-react";
import useVoiceRecorder from "@/lib/useVoiceRecorder";

const KIND_META = {
  fall: { l: "Fall", icon: Activity, color: "#EF4444",
    endpoint: (cid) => `/clients/${cid}/incidents/fall` },
  injury: { l: "Injury", icon: AlertOctagon, color: "#B91C1C",
    endpoint: (cid) => `/clients/${cid}/incidents/injury` },
  behaviour: { l: "Behaviour", icon: Smile, color: "#F59E0B",
    endpoint: (cid) => `/clients/${cid}/incidents/behaviour` },
  medication: { l: "Medication incident", icon: Pill, color: "#7C3AED",
    endpoint: (cid) => `/clients/${cid}/incidents/medication` },
  hospital_transfer: { l: "Hospital transfer", icon: Ambulance, color: "#B91C1C",
    endpoint: (cid) => `/clients/${cid}/hospital-transfers` },
  body_check: { l: "Body check", icon: ListChecks, color: "#0EA5E9",
    endpoint: (cid) => `/clients/${cid}/body-check` },
};

function pruneNulls(obj) {
  return Object.fromEntries(
    Object.entries(obj).filter(([, v]) => v !== null && v !== undefined && v !== "")
  );
}

function Row({ label, value }) {
  if (value == null || value === "" || (Array.isArray(value) && value.length === 0)) return null;
  return (
    <div className="grid grid-cols-[7rem_1fr] gap-2 py-1 text-sm">
      <span className="text-clinical-subtext text-xs uppercase tracking-wide">{label}</span>
      <span className="text-clinical-text">
        {Array.isArray(value) ? value.join(", ") :
         typeof value === "boolean" ? (value ? "Yes" : "No") :
         String(value)}
      </span>
    </div>
  );
}

function renderFields(result) {
  switch (result.kind) {
    case "fall":
      return <>
        <Row label="Location" value={result.location} />
        <Row label="Witnessed" value={result.witnessed} />
        <Row label="Body part" value={result.body_part} />
        <Row label="Severity" value={result.injury_severity} />
        <Row label="ER" value={result.er_transferred} />
        <Row label="First aid" value={result.first_aid_given} />
      </>;
    case "injury":
      return <>
        <Row label="Location" value={result.location} />
        <Row label="Mechanism" value={result.injury_mechanism} />
        <Row label="Severity" value={result.injury_severity} />
        <Row label="Body part" value={result.body_part} />
        <Row label="Witnessed" value={result.witnessed} />
        <Row label="First aid" value={result.first_aid_given} />
        <Row label="Photo" value={result.photo_taken} />
        <Row label="ER" value={result.er_transferred} />
      </>;
    case "behaviour":
      return <>
        <Row label="Location" value={result.location} />
        <Row label="Antecedent" value={result.antecedent} />
        <Row label="Intervention" value={result.intervention} />
        <Row label="Aftermath" value={result.aftermath} />
        <Row label="Duration" value={result.behaviour_duration_minutes ? `${result.behaviour_duration_minutes} min` : null} />
        <Row label="Function" value={result.function} />
        <Row label="PRN given" value={result.prn_administered} />
      </>;
    case "medication":
      return <>
        <Row label="Medication" value={result.med_name} />
        <Row label="Dose" value={result.med_dose} />
        <Row label="Error type" value={result.med_error_type?.replace(/_/g, " ")} />
        <Row label="Pharmacy notified" value={result.pharmacy_contacted} />
        <Row label="Doctor notified" value={result.doctor_contacted} />
      </>;
    case "hospital_transfer":
      return <>
        <Row label="Reason" value={result.reason} />
        <Row label="Type" value={result.transfer_type} />
        <Row label="Transport" value={result.transport_method} />
        <Row label="Facility" value={result.receiving_facility} />
        <Row label="Belongings" value={result.belongings} />
        <Row label="Documents" value={result.documents_sent} />
      </>;
    case "body_check":
      return <>
        <Row label="Integrity" value={result.overall_skin_integrity} />
        {(result.body_check_findings || []).map((f, i) => (
          <Row key={i} label={`Finding #${i+1}`}
            value={`${f.area?.replace(/_/g," ")}: ${f.finding}${f.note ? ` — ${f.note}` : ""}`} />
        ))}
      </>;
    default:
      return <Row label="Description" value={result.description} />;
  }
}

function buildPayload(result, clientId) {
  // Build the POST body for the appropriate endpoint. Common fields first.
  const today = new Date().toISOString();
  const common = pruneNulls({
    location: result.location,
    description: result.description || result.transcript?.slice(0, 280),
    occurred_at: result.occurred_at || today,
    family_notified: result.family_notified ?? false,
    manager_notified: result.manager_notified ?? false,
  });
  switch (result.kind) {
    case "fall":
      return { ...common, ...pruneNulls({
        witnessed: result.witnessed ?? false,
        injury_severity: result.injury_severity || "none",
        body_part: result.body_part || "",
        er_transferred: result.er_transferred ?? false,
        prevention_plan: "",
      })};
    case "injury":
      return { ...common, ...pruneNulls({
        injury_mechanism: result.injury_mechanism || "other",
        injury_severity: result.injury_severity || "minor",
        body_part: result.body_part || "",
        witnessed: result.witnessed ?? false,
        first_aid_given: result.first_aid_given ?? false,
        photo_taken: result.photo_taken ?? false,
        er_transferred: result.er_transferred ?? false,
      })};
    case "behaviour":
      return { ...common, ...pruneNulls({
        antecedent: result.antecedent || "",
        behaviour_duration_minutes: result.behaviour_duration_minutes || 0,
        intervention: result.intervention || "",
        aftermath: result.aftermath || "",
        function: result.function || "unknown",
        prn_administered: result.prn_administered ?? false,
        prevention_plan: "",
      })};
    case "medication":
      if (!result.med_name) {
        // Backend requires med_name — synthesize from description if missing
        return null;
      }
      return { ...common, ...pruneNulls({
        med_name: result.med_name,
        med_dose: result.med_dose || "",
        med_error_type: result.med_error_type || "missed_dose",
        pharmacy_contacted: result.pharmacy_contacted ?? false,
        doctor_contacted: result.doctor_contacted ?? false,
      })};
    case "hospital_transfer":
      return pruneNulls({
        reason: result.reason || result.description || "Transferred to hospital",
        transfer_type: result.transfer_type || "ER",
        transport_method: result.transport_method || "911",
        receiving_facility: result.receiving_facility || "",
        receiving_facility_phone: result.receiving_facility_phone || "",
        belongings: result.belongings || "",
        documents_sent: result.documents_sent || [],
        family_notified: result.family_notified ?? false,
        notes: result.description || "",
      });
    case "body_check":
      return pruneNulls({
        findings: (result.body_check_findings || []).map((f) => ({
          area: f.area, finding: f.finding, note: f.note || "",
        })),
        overall_skin_integrity: result.overall_skin_integrity || "intact",
        notes: result.description || "",
      });
    default:
      return null;
  }
}

export default function VoiceIncidentModal({ open, clientId, onClose, onSaved }) {
  const [result, setResult] = useState(null);
  const [kindOverride, setKindOverride] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  const rec = useVoiceRecorder({
    endpoint: "/ai/voice-fill/incident",
    extra: { client_id: clientId },
    filenamePrefix: "incident",
    onResult: (data) => { setResult(data); setKindOverride(data.kind); },
  });

  const close = () => {
    rec.reset(); setResult(null); setKindOverride(null); onClose?.();
  };

  const submit = async () => {
    const merged = { ...result, kind: kindOverride || result.kind };
    const meta = KIND_META[merged.kind];
    if (!meta) {
      toast.error("Choose an incident type first");
      return;
    }
    const body = buildPayload(merged, clientId);
    if (!body) {
      toast.error("Missing required fields — edit manually after applying");
      return;
    }
    setSubmitting(true);
    try {
      await api.post(meta.endpoint(clientId), body);
      toast.success(`${meta.l} report saved`);
      onSaved?.();
      close();
    } catch (e) {
      toast.error(e?.response?.data?.detail || `Could not save ${meta.l}`);
    } finally {
      setSubmitting(false);
    }
  };

  if (!open) return null;

  const activeKind = kindOverride || result?.kind || "unknown";
  const meta = KIND_META[activeKind];

  return createPortal(
    <div className="fixed inset-0 z-[120] flex items-center justify-center bg-black/60 backdrop-blur-sm p-4"
      data-testid="voice-incident-modal">
      <div className="card-surface w-full max-w-xl max-h-[90vh] overflow-hidden flex flex-col">
        <div className="flex items-center justify-between border-b border-clinical-border px-5 py-4">
          <div className="flex items-center gap-3">
            <div className="flex h-9 w-9 items-center justify-center rounded-xl"
              style={{ background: "#EF44441a", color: "#EF4444" }}>
              <Sparkles size={18} />
            </div>
            <div>
              <h2 className="font-semibold text-clinical-text">Voice incident report</h2>
              <p className="text-xs text-clinical-subtext">
                {rec.isIdle && !result && "Describe what happened — AI picks the report type."}
                {rec.isRecording && `Recording · ${rec.mmss}`}
                {rec.isProcessing && "Classifying + extracting…"}
                {result && `Detected: ${meta?.l || "Unknown"}`}
              </p>
            </div>
          </div>
          <button type="button" onClick={close}
            className="rounded-lg p-2 text-clinical-subtext hover:bg-clinical-bg"
            aria-label="Close" data-testid="voice-incident-close">
            <X size={18} />
          </button>
        </div>

        <div className="overflow-y-auto flex-1 p-5 space-y-3">
          {!result && (
            <div className="flex flex-col items-center gap-4 py-6">
              {rec.isIdle && (
                <p className="text-sm text-center text-clinical-subtext max-w-md">
                  Example: <i>"Margaret fell in the bathroom around 3 PM. Hit her right hip, no skin tear, pain 2 out of 10. She's up and walking now. I notified her daughter and the manager."</i>
                </p>
              )}
              {rec.isRecording && (
                <div className="flex flex-col items-center gap-2">
                  <div className="relative h-20 w-20">
                    <div className="absolute inset-0 rounded-full bg-red-500/20 animate-ping" />
                    <div className="relative h-20 w-20 rounded-full bg-red-500 flex items-center justify-center">
                      <Mic className="h-8 w-8 text-white" />
                    </div>
                  </div>
                  <div className="text-2xl font-mono tabular-nums text-clinical-text" data-testid="voice-incident-timer">
                    {rec.mmss}
                  </div>
                </div>
              )}
              {rec.isProcessing && <Loader2 className="h-10 w-10 animate-spin text-clinical-accent" />}
              <button
                type="button"
                onClick={rec.toggle}
                disabled={rec.isProcessing}
                className={`inline-flex items-center gap-2 rounded-xl px-5 py-3 text-sm font-semibold transition ${
                  rec.isRecording ? "bg-red-600 text-white hover:bg-red-700"
                                  : "bg-clinical-accent text-white hover:opacity-90"
                } disabled:opacity-50`}
                data-testid={rec.isRecording ? "voice-incident-stop" : "voice-incident-start"}
              >
                {rec.isRecording ? <Square className="h-4 w-4 fill-current" /> : <Mic className="h-4 w-4" />}
                {rec.isRecording ? "Stop & process" : rec.isProcessing ? "Processing…" : "Start recording"}
              </button>
            </div>
          )}

          {result && (
            <div className="space-y-3" data-testid="voice-incident-results">
              {result.transcript && (
                <details className="card-surface p-3 bg-clinical-bg/40">
                  <summary className="cursor-pointer text-xs font-semibold uppercase text-clinical-subtext">
                    Transcript
                  </summary>
                  <p className="mt-2 text-sm text-clinical-text whitespace-pre-line">{result.transcript}</p>
                </details>
              )}
              {(result.warnings || []).length > 0 && (
                <div className="rounded-lg border border-amber-200 bg-amber-50 p-2.5 text-xs text-amber-900">
                  <div className="flex items-center gap-1.5 font-semibold mb-1">
                    <AlertTriangle size={12} /> Warnings
                  </div>
                  <ul className="list-disc pl-5 space-y-0.5">
                    {result.warnings.map((w, i) => <li key={i}>{w}</li>)}
                  </ul>
                </div>
              )}

              {/* Kind override picker */}
              <div>
                <div className="label-eyebrow mb-2">Report type {result.confidence ? `· ${Math.round(result.confidence * 100)}% confidence` : ""}</div>
                <div className="grid grid-cols-3 gap-1.5">
                  {Object.entries(KIND_META).map(([k, m]) => {
                    const I = m.icon;
                    const active = activeKind === k;
                    return (
                      <button key={k} type="button" onClick={() => setKindOverride(k)}
                        className={`flex flex-col items-center justify-center gap-1 rounded-lg border py-2 text-xs font-medium transition ${
                          active
                            ? "border-clinical-accent bg-clinical-accent/10 text-clinical-accent"
                            : "border-clinical-border bg-white text-clinical-subtext hover:text-clinical-text"
                        }`}
                        data-testid={`voice-incident-kind-${k}`}
                      >
                        <I size={14} style={{ color: m.color }} />
                        <span>{m.l}</span>
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Field preview */}
              {meta && (
                <div className="card-surface p-3">
                  <div className="label-eyebrow mb-2">Extracted fields</div>
                  <Row label="Description" value={result.description} />
                  {renderFields(result)}
                </div>
              )}
            </div>
          )}
        </div>

        {result && meta && (
          <div className="border-t border-clinical-border px-5 py-3 flex items-center justify-between">
            <button type="button" onClick={() => { setResult(null); setKindOverride(null); }}
              className="text-sm text-clinical-subtext hover:text-clinical-text"
              data-testid="voice-incident-redo">
              Record again
            </button>
            <button type="button" onClick={submit} disabled={submitting}
              className="btn-primary" data-testid="voice-incident-submit">
              {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : <meta.icon className="h-4 w-4" />}
              Save as {meta.l}
            </button>
          </div>
        )}
      </div>
    </div>,
    document.body,
  );
}
